import { Injectable } from "@angular/core";
import { UsuarioLogadoModel } from "../models/usuario-logado";
import { BehaviorSubject } from "rxjs";
import { HttpClient } from "@angular/common/http";
import { Capacitor } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { UsuarioAutenticado } from "../models/auth.models";
import { environment } from '../../../environments/environment';
import { PreferenciasService } from './preferencias-service';

@Injectable({
  providedIn: 'root'
})
export class DadosUsuarioService {
    private dadosUsuario?: UsuarioLogadoModel;
    private dadosUsuarioSubject = new BehaviorSubject<UsuarioLogadoModel | undefined>(undefined);
    public dadosUsuario$ = this.dadosUsuarioSubject.asObservable();

    private geolocalizacaoManual = false;
    
    constructor(
      private http: HttpClient,
      private preferenciasService: PreferenciasService
    ){}

    public async atualizarUsuario(usuario: UsuarioLogadoModel): Promise<void> {
        this.dadosUsuario = usuario;
        this.dadosUsuarioSubject.next(usuario);

        const preferencias = await this.preferenciasService.obter();
        if (preferencias.localizacaoAtual)
          await this.atualizarLocalizacao(preferencias.localizacaoAtual.descricao,preferencias.localizacaoAtual.latitude,preferencias.localizacaoAtual.longitude);
        else
          this.obterLocalizacao();
    }

    public async atualizarUsuarioLogon(usuario: UsuarioAutenticado): Promise<void> {
      this.dadosUsuario = {
        nome: usuario.nome,
        email: usuario.email,
        foto: usuario.foto
      };

      this.dadosUsuarioSubject.next(this.dadosUsuario);

      const preferencias = await this.preferenciasService.obter();
      if (preferencias.localizacaoAtual)
        await this.atualizarLocalizacao(preferencias.localizacaoAtual.descricao,preferencias.localizacaoAtual.latitude,preferencias.localizacaoAtual.longitude);
      else
        this.obterLocalizacao();
    }

    public obterUsuario(): UsuarioLogadoModel | undefined {
        return this.dadosUsuario;
    }

    public async atualizarLocalizacao(descricao: string, latitude: number, longitude: number): Promise<void> {
      if (!this.dadosUsuario) { return; }

      this.geolocalizacaoManual = true;

      const usuarioAtualizado: UsuarioLogadoModel = {
        ...this.dadosUsuario,
        latitude,
        longitude,
        localizacao: 'Carregando localização...'
      };

      this.dadosUsuario = usuarioAtualizado;
      this.dadosUsuarioSubject.next(usuarioAtualizado);

      await this.preferenciasService.adicionarHistoricoLocalizacao(
        descricao,
        latitude,
        longitude
      );

      await this.buscarEndereco(latitude, longitude);
    }

    public usarLocalizacaoAtual(): void {
      void this.obterLocalizacao();
    }


    private async obterLocalizacao(): Promise<void> {
      this.geolocalizacaoManual = false;
      try {

        let latitude: number;
        let longitude: number;

        let localizacaoAtiva = await this.verificarGeolocalizacaoAtiva();
        let gpsConfirmado = false;

        if (!localizacaoAtiva) {
            console.log('Permissão de localização não concedida.');
            latitude = environment.localizacaoPadrao.latitude;
            longitude = environment.localizacaoPadrao.longitude;
        }
        else 
        {

          if (Capacitor.isNativePlatform()) {
            const position = await Geolocation.getCurrentPosition({
              enableHighAccuracy: true
            });

            latitude = position.coords.latitude;
            longitude = position.coords.longitude;
            gpsConfirmado = true;
          } else {
            try{
              const position = await new Promise<GeolocationPosition>(
                (resolve, reject) => {

                  navigator.geolocation.getCurrentPosition(
                    resolve,
                    reject,
                    {
                      enableHighAccuracy: true,
                      timeout: 10000,
                      maximumAge: 0
                    }
                  );

                }
              );

              latitude = position.coords.latitude;
              longitude = position.coords.longitude;
              gpsConfirmado = true;
            }
            catch (error) {
              latitude = environment.localizacaoPadrao.latitude;
              longitude = environment.localizacaoPadrao.longitude;
            }
          }
        }

        const usuario = this.dadosUsuario;

        if (!usuario) {
          return;
        }

        const usuarioAtualizado: UsuarioLogadoModel = {
          ...usuario,
          latitude,
          longitude
        };

        this.dadosUsuario = usuarioAtualizado;
        this.dadosUsuarioSubject.next(usuarioAtualizado);

        const preferencias = await this.preferenciasService.obter();
        preferencias.localizacaoAtual = null;
        this.preferenciasService.salvar(preferencias);

        await this.buscarEndereco(latitude, longitude);

      } catch (error) {

        console.log(
          'Não foi possível obter a localização.',
          error
        );

      }
    }

    private async buscarEndereco(latitude: number, longitude: number): Promise<void> {
      if (!this.dadosUsuario) { return; }
      let localizacaoAtiva = await this.verificarGeolocalizacaoAtiva();

      if (!localizacaoAtiva && !this.geolocalizacaoManual) {
        this.dadosUsuario.localizacao = 'Escolher localização';
        this.dadosUsuarioSubject.next(this.dadosUsuario);
        return;
      }

      const url =
        `https://nominatim.openstreetmap.org/reverse` +
        `?lat=${latitude}` +
        `&lon=${longitude}` +
        `&format=json` +
        `&addressdetails=1`;

      this.http.get<any>(url).subscribe({
        next: resultado => {

          const endereco = resultado.address;

          this.dadosUsuario!.localizacao =
            endereco.suburb
              ? `${endereco.suburb}, ${endereco.city ?? endereco.town ?? endereco.village} - ${endereco.state}`
              : `${endereco.city ?? endereco.town ?? endereco.village} - ${endereco.state}`;

          this.dadosUsuarioSubject.next(this.dadosUsuario);
        },
        error: error => {
          console.log('Não foi possível obter o endereço.', error);
        }
      });
    }

    public async verificarGeolocalizacaoAtiva(): Promise<boolean> {
    try {
      let ativa = false;

      if (Capacitor.isNativePlatform()) {
        const permissao = await Geolocation.checkPermissions();
        ativa = permissao.location === 'granted';
      } else if (navigator.permissions) {
        const permissao = await navigator.permissions.query({
          name: 'geolocation'
        });
        ativa = permissao.state !== 'denied';
      }
      return ativa;

    } catch (error) {
      console.error('Erro ao verificar permissão de localização:', error);
      return false;
    }
  }
}

